import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Grid,
  Image,
  Text,
  Button,
  HStack,
  IconButton,
  useColorModeValue,
  Flex,
  VStack,
  Divider,
  CloseButton,
  ModalFooter
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { updateUserEquipment, uploadUserEquipmentImage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function SelectedEquipmentModal({ item, config, onClose, onPreferenceChange, onUserEquipmentChange, isTsubo }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { currentUser, isFunctionEnabled } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(item.images && item.images.length > 0 ? item.images : []);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentIframeSrc, setCurrentIframeSrc] = useState('');

  // 画像URLの配列を取得
  const imageUrls = useMemo(() => {
    if (!item) return [];
    
    // 新形式（images配列）をチェック
    if (item.images && item.images.length > 0) {
      return item.images;
    }
    
    // 旧形式（個別のURL）をチェック
    const urls = [];
    for (let i = 1; i <= 5; i++) {
      const url = item[`image${i}_sas_url`];
      if (url) urls.push(url);
    }
    return urls;
  }, [item]);

  useEffect(() => {
    setImages(item.images && item.images.length > 0 ? item.images : []);
  }, [item]);

  useEffect(() => {
    setCurrentIndex(0);
    if (item && item.is_user_equipment) {
      setEditValues({ ...item });
    }
  }, [item]);

  const goToNext = useCallback(() => {
    if (imageUrls.length > 1) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
    }
  }, [imageUrls.length]);

  const goToPrevious = useCallback(() => {
    if (imageUrls.length > 1) {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + imageUrls.length) % imageUrls.length);
    }
  }, [imageUrls.length]);

  useEffect(() => {
    if (imageUrls.length > 1) {
      const timer = setTimeout(goToNext, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, imageUrls.length, goToNext]);

  if (!item) return null;

  const handleLikeClick = (e) => {
    e.stopPropagation();
    onPreferenceChange(item.id, item.item_type, { is_liked: !item.is_liked });
  };
  
  const handleFixClick = (e) => {
    e.stopPropagation();
    onPreferenceChange(item.id, item.item_type, { is_fixed: !item.is_fixed });
  };

  const handlePrevImageClick = (e) => { e.stopPropagation(); goToPrevious(); };
  const handleNextImageClick = (e) => { e.stopPropagation(); goToNext(); };

  // Chakra UIのカラーモードに基づく色の設定
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '価格未定';
    return `¥${price.toLocaleString()}`;
  };

  const handleSave = async () => {
    try {
      // 編集可能なフィールドのみ抽出
      const editableFieldNames = config.fields
        .filter(field =>
          ![
            'id', 'user_id', 'created_at', 'updated_at', 'is_liked', 'is_fixed', 'locked_at',
            'signature_url', 'memo', 'material_files', 'preferenceId', 'recommended'
          ].includes(field.name)
        )
        .map(field => field.name);

      const payload = {};
      editableFieldNames.forEach(name => {
        if (editValues.hasOwnProperty(name)) {
          payload[name] = editValues[name];
        }
      });
      payload.user_id = item.user_id;
      await updateUserEquipment(
        item.item_type,
        item.id,
        payload
      );
      setEditMode(false);
      if (onUserEquipmentChange) {
        onUserEquipmentChange();
      }
      if (onClose) {
        onClose(); // 編集完了後にモーダルを閉じる
      }
    } catch (e) {
      alert('保存に失敗しました');
    }
  };

  const handleEditFloorplan = () => {
    const url = (item.user_id && item.user_id !== currentUser.uid) ? `/admin/user/${item.user_id}/planner` : '/planner';
    navigate(url, { state: { floorRooms: item.rooms || [], targetUserId: item.user_id, mode: 'edit', user_equipment_id: item.id } });
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="800px" maxH="90vh" overflowY="auto">
        <ModalHeader borderBottomWidth="1px">
          {item.manufacturer} - {item.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={4}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            {/* 画像セクション */}
            <Box position="relative" borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden" minH="300px">
              {imageUrls.length > 0 ? (
                <>
                  <Image
                    src={imageUrls[currentIndex]}
                    alt={`${item.name} - 画像 ${currentIndex + 1}`}
                    w="100%"
                    h="300px"
                    objectFit="contain"
                    cursor="pointer"
                    onClick={() => setIsImageModalOpen(true)}
                  />
                  {imageUrls.length > 1 && (
                    <>
                      <IconButton
                        icon={<ChevronLeftIcon />}
                        aria-label="前の画像"
                        position="absolute"
                        left={2}
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={handlePrevImageClick}
                        colorScheme="blackAlpha"
                      />
                      <IconButton
                        icon={<ChevronRightIcon />}
                        aria-label="次の画像"
                        position="absolute"
                        right={2}
                        top="50%"
                        transform="translateY(-50%)"
                        onClick={handleNextImageClick}
                        colorScheme="blackAlpha"
                      />
                    </>
                  )}
                </>
              ) : (
                <Flex
                  w="100%"
                  h="300px"
                  bg="gray.100"
                  align="center"
                  justify="center"
                  color="gray.500"
                >
                  画像なし
                </Flex>
              )}
            </Box>

            {/* 詳細情報セクション */}
            <VStack align="stretch" spacing={4}>
              {item.is_user_equipment && editMode ? (
                <>
                  {config.fields?.map(field => {
                    if ([
                      'id', 'user_id', 'created_at', 'updated_at', 'images', 'signature_url', 'locked_at', 'preferenceId', 'is_liked', 'is_fixed', 'memo', 'material_files', 'recommended'
                    ].includes(field.name)) return null;
                    // boolean型
                    if (field.type === 'boolean') {
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                          <input
                            type="checkbox"
                            checked={!!editValues[field.name]}
                            onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.checked }))}
                          />
                        </Box>
                      );
                    }
                    // select型
                    if (field.type === 'select') {
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                          <select
                            value={editValues[field.name] || ''}
                            onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.value }))}
                          >
                            <option value="">選択してください</option>
                            {field.options?.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </Box>
                      );
                    }
                    // number/string型
                    return (
                      <Box key={field.name}>
                        <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                        <input
                          type={field.type === 'number' ? 'number' : 'text'}
                          value={editValues[field.name] ?? ''}
                          onChange={e => setEditValues(v => ({ ...v, [field.name]: field.type === 'number' ? Number(e.target.value) : e.target.value }))}
                        />

                      </Box>
                    );
                
                  })}
                  {/* 画像アップロードボックス（ユーザ登録設備の場合のみ） */}
                  <Box mt={2}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        try {
                          setUploading(true);
                          const user = currentUser;
                          const res = await uploadUserEquipmentImage(
                            item.item_type,
                            file,
                            user,
                            item.user_id
                          );
                          setEditValues(v => ({ ...v, images: [...images, res.url] }));
                          setImages(v => [...v, res.url]);
                        } catch (err) {
                          alert('画像のアップロードに失敗しました');
                        } finally {
                          setUploading(false);
                        }
                      }}
                      disabled={uploading}
                    />
                    {uploading && <Text fontSize="xs" color="gray.500">アップロード中...</Text>}
                    {images && (
                      <Box mt={1}>
                        {images.map((url, index) => (
                          <Box key={url} position="relative" display="inline-block" mr={2} mb={2}>
                            <img src={url} alt={`設備画像 ${index + 1}`} style={{ maxWidth: 120, maxHeight: 120 }} />
                            <CloseButton
                              size="sm"
                              position="absolute"
                              top="2px"
                              right="2px"
                              color="red.500"
                              bg="whiteAlpha.800"
                              borderRadius="full"
                              onClick={() => {
                                setImages(images.filter((_, i) => i !== index))
                                setEditValues(v => ({ ...v, images: images.filter((_, i) => i !== index) }))
                              }}
                              zIndex={1}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <HStack>
                    <Button bg="black" color="white" onClick={handleSave}>保存</Button>
                    <Button onClick={() => setEditMode(false)}>キャンセル</Button>
                  </HStack>
                </>
              ) : (
                <>
                  <Box>
                    <Text fontSize="sm" color={subTextColor}>メーカー</Text>
                    <Text fontSize="lg" fontWeight="bold">{item.manufacturer}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={subTextColor}>商品名</Text>
                    <Text fontSize="lg" fontWeight="bold">{item.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={subTextColor}>価格</Text>
                    <Text fontSize="lg" fontWeight="bold">
                      {formatPrice(item.price)}
                    </Text>
                  </Box>
                  {item.features && (
                    <Box>
                      <Text fontSize="sm" color={subTextColor}>特徴</Text>
                      <Text>{item.features}</Text>
                    </Box>
                  )}
                  {config.fields?.map(field => {
                    const value = item[field.name];
                    if (value === undefined || value === null || field.name=='price' || field.name=='features' || field.name=='recommended' || field.name=='manufacturer' || field.name=='name' || field.name=='images') return null;
                    // floorplanのm2のみ単位切替
                    if (config.key === 'floorplan' && field.unit === 'm2') {
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor}>{field.label}{isTsubo ? '（坪）' : '（m²）'}</Text>
                          <Text>{isTsubo ? value*0.3025 + '坪' : value + 'm²'}</Text>
                        </Box>
                      );
                    }
                    return (
                      <Box key={field.name}>
                        <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                        <Text>{typeof value === 'boolean' ? (value ? 'あり' : 'なし') : value}{field.unit || ''}</Text>
                      </Box>
                    );
                  })}
                  
                  <Divider />
                  {item.is_user_equipment && (
                    <Button colorScheme="gray" onClick={() => setEditMode(true)} disabled={isFunctionEnabled('lock') ? !!item.locked_at : false}>{config.key==='floorplan' ? '情報を編集' : '編集'}</Button>
                  )}
                  {item.is_user_equipment && item.item_type === 'floorplan' && !editMode && isFunctionEnabled('floor_plan') && (
                    <Button bg="black" color="white" onClick={handleEditFloorplan} mb={2}>間取りを編集</Button>
                  )}

                  <HStack spacing={4}>
                    <Button
                      leftIcon={item.is_liked ? '❤️' : '🤍'}
                      colorScheme="pink"
                      variant={item.is_liked ? "solid" : "outline"}
                      onClick={handleLikeClick}
                    >
                      {item.is_liked ? "お気に入り解除" : "お気に入り"}
                    </Button>
                    <Button
                      leftIcon={item.is_fixed ? '★' : '☆'}
                      colorScheme={item.is_fixed ? "yellow" : "gray"}
                      variant={item.is_fixed ? "solid" : "outline"}
                      onClick={handleFixClick}
                      isDisabled={isFunctionEnabled('lock') ? item.locked_at : false}
                    >
                      {item.is_fixed ? "暫定解除" : "暫定"}
                    </Button>
                  </HStack>
                </>
              )}
            </VStack>
          </Grid>
        </ModalBody>
      </ModalContent>

      {/* 画像拡大表示用のモーダル */}
      <Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" w="100vw" h="100vh" m={0}>
          <ModalCloseButton color="white" />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
            {imageUrls.length > 1 && (
              <>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  aria-label="前の画像"
                  position="absolute"
                  left={2}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={handlePrevImageClick}
                  colorScheme="blackAlpha"
                />
                <IconButton
                  icon={<ChevronRightIcon />}
                  aria-label="次の画像"
                  position="absolute"
                  right={2}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={handleNextImageClick}
                  colorScheme="blackAlpha"
                />
              </>
            )}
            <Image
              src={imageUrls[currentIndex]}
              alt={`${item.name} - 画像 ${currentIndex + 1}`}
              w="100%"
              h="100vh"
              objectFit="contain"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Modal>
  );
} 